import React, { useState } from "react";
import makeAnimated from "react-select/animated";
import Select from "react-select";
import { CampaignTypes } from "../../../models/Campaign";
import { UseFormRegister } from "react-hook-form/dist/types/form";
import { FieldErrors, SubmitHandler } from "react-hook-form";
import { UseMutationResult } from "react-query";
import MultiRangeSlider from "multi-range-slider-react";
import "./Styles.css";
import { useTranslation } from "react-i18next";

const animatedComponents = makeAnimated();

type Props = {
  campaign: CampaignTypes;
  setCampaign: React.Dispatch<React.SetStateAction<CampaignTypes>>;
  register: UseFormRegister<CampaignTypes>;
  errors?: FieldErrors<CampaignTypes>;
  categories: string[];
  countries: string[];
  brands: any;
  products: any;
  onSubmit: SubmitHandler<CampaignTypes>;
  handleSubmit: (
    onSubmit: SubmitHandler<CampaignTypes>
  ) => (e: React.BaseSyntheticEvent) => void;
  addCampaignMutation: UseMutationResult<any, unknown, CampaignTypes, unknown>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  isValid: boolean;
};

const StepThree = ({
  campaign,
  setCampaign,
  register,
  errors,
  products,
  brands,
  countries,
  categories,
  onSubmit,
  handleSubmit,
  setStep,
  isValid,
  addCampaignMutation,
}: Props) => {
  const { t } = useTranslation();
  const TOGGLE_BUTTONS = {
    brands: t("stepThree.chooseBrands"),
    productIds: t("stepThree.specifyProduct"),
    category: t("stepThree.category"),
    gender: t("stepThree.gender"),
    age: t("stepThree.ageRange"),
    country: t("stepThree.country"),
  } as const;

  
  type ToggleButtonKey = keyof typeof TOGGLE_BUTTONS;

  
  const [selectedBrands, setSelectedBrands] = useState<string[]>(
    campaign?.parameters?.brands || []
  );
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    campaign?.parameters?.productIds || []
  );

  const [ageValue, setAgeValue] = useState<Array<number>>(
    campaign?.parameters?.age || [19, 35]
  );

  const ageChangeHandler = (e: {
    min?: number;
    max?: number;
    minValue: any;
    maxValue: any;
  }) => {
    const campaignParams: any = campaign.parameters;

    campaignParams["age"] = [e.minValue, e.maxValue];

    setCampaign({
      ...campaign,
      parameters: campaignParams,
    });

    setAgeValue([e.minValue, e.maxValue]);
  };

  const [toggleAction, setToggleAction] = useState<any>({
    brands: !!campaign?.parameters?.brands,
    productIds: !!campaign?.parameters?.productIds,
    category: !!campaign?.parameters?.category,
    gender: !!campaign?.parameters?.gender,
    age: !!campaign?.parameters?.age,
    country: !!campaign?.parameters?.country,
  });

  return (
    <div className="bg-gray-100 px-2 pb-4 rounded-b-md">
      <div className="pt-5 grid grid-cols-5 gap-2">
        {Object.keys(toggleAction).map((key: string) => {
          const typedKey = key as ToggleButtonKey;
          return (
            <button
              key={key}
              type={"button"}
              className={`w-full py-2 focus:outline-none rounded-md me-2 ${
                toggleAction[key]
                  ? "bg-indigo-200 border-2 border-indigo-300 hover:bg-indigo-300 text-indigo-600"
                  : "rounded-md border-2 border-indigo-300 bg-gray-50 text-gray-500 hover:bg-indigo-100"
              }`}
              onClick={() => {
                setToggleAction((prev: { [x: string]: any }) => ({
                  ...prev,
                  [key]: !prev[key],
                }));

                const campaignParams: any = campaign.parameters;

                if (!toggleAction[key]) {
                  campaignParams[key] = "";
                } else {
                  delete campaignParams[key];
                }

                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              {TOGGLE_BUTTONS[typedKey]}
            </button>
          );
        })}
      </div>

      {toggleAction.brands && (
        <div className="mt-5">
          <label
            htmlFor="campaignName"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.chooseBrands")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <Select
              closeMenuOnSelect={false}
              components={animatedComponents}
              isMulti
              value={brands?.filter((item: any) =>
                selectedBrands.includes(item.value)
              )}
              onChange={(e) => {
                setSelectedBrands(e.map((item: any) => item.value));
                const campaignParams: any = campaign.parameters;
                campaignParams["brands"] = e.map((item: any) => item.value);
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
              options={brands}
              className="focus:outline-none w-full me-2"
            />
          </div>
        </div>
      )}

      {toggleAction.productIds && (
        <div className="mt-5">
          <label
            htmlFor="campaignDate"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.specifyProduct")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <Select
              closeMenuOnSelect={false}
              components={animatedComponents}
              isMulti
              value={products.filter((item: any) =>
                selectedProducts.includes(item.value)
              )}
              onChange={(e) => {
                setSelectedProducts(e.map((item: any) => item.value));
                const campaignParams: any = campaign.parameters;

                campaignParams["productIds"] = e.map((item: any) => item.value);

                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
              options={products}
              className="focus:outline-none w-full me-2"
            />
          </div>
        </div>
      )}

      {toggleAction.hasWarranty && (
        <div className="mt-5">
          <label
            htmlFor="warranty"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.hasWarranty")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="warranty"
              name="warranty"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["hasWarranty"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      )}

      {toggleAction.warrantyEndDate && (
        <div className="mt-5">
          <label
            htmlFor="warrantyStart"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.warrantyEnd")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <input
              type="date"
              id="warrantyStart"
              name="warrantyStart"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["warrantyEndDate"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            />
          </div>
        </div>
      )}

      {toggleAction.hasInsurance && (
        <div className="mt-5">
          <label
            htmlFor="insurance"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.hasInsurance")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="insurance"
              name="insurance"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["hasInsurance"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="">{t("stepThree.selectInsurance")}</option>
              <option value="yes">{t("stepThree.yes")}</option>
              <option value="no">{t("stepThree.no")}</option>
            </select>
          </div>
        </div>
      )}

      {toggleAction.condition && (
        <div className="mt-5">
          <label
            htmlFor="condition"
            className="block text-sm font-bold text-gray-700"
          >
            Condition <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="warranty"
              name="warranty"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["condition"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="">{t("stepThree.selectCondition")}</option>
              <option value="poor">{t("stepThree.poor")}</option>
              <option value="used">{t("stepThree.used")}</option>
              <option value="likeNew">{t("stepThree.likeNew")}</option>
              <option value="brandNew">{t("stepThree.brandNew")}</option>
            </select>
          </div>
        </div>
      )}

      {toggleAction.category && (
        <div className="mt-5">
          <label
            htmlFor="category"
            className="block text-sm font-bold text-gray-700"
          >
            Category <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="category"
              name="category"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["category"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="">{t("stepThree.selectCategory")}</option>
              {categories.map((category: string) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {toggleAction.gender && (
        <div className="mt-5">
          <label
            htmlFor="gender"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.gender")} <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="gender"
              name="gender"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;

                campaignParams["gender"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
              value={campaign.parameters?.["gender"]}
            >
              <option value="">{t("stepThree.selectGender")}</option>
              <option value="male">{t("stepThree.male")}</option>
              <option value="female">{t("stepThree.female")}</option>
              <option value="dontSpecify">{t("stepThree.dontSpecify")}</option>
            </select>
          </div>
        </div>
      )}

      {toggleAction.age && (
        <div className="mt-5">
          <label
            htmlFor="age"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.ageRange", { min: ageValue[0], max: ageValue[1] })}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <MultiRangeSlider
              canMinMaxValueSame={false}
              min={0}
              max={100}
              step={5}
              minValue={ageValue[0]}
              maxValue={ageValue[1]}
              onInput={(e) => {
                ageChangeHandler(e);
              }}
              label={false}
              ruler={false}
              className="age-range-slider"
              barInnerColor={"#6366F1"}
              thumbLeftColor={"#6366F1"}
              thumbRightColor={"#6366F1"}
              barLeftColor={"#D1D5DB"}
              barRightColor={"#D1D5DB"}
            />
          </div>
        </div>
      )}

      {toggleAction.country && (
        <div className="mt-5">
          <label
            htmlFor="country"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.country")} <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="country"
              name="country"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["country"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="">{t("stepThree.selectCountry")}</option>
              {countries?.map((country: any) => (
                <option key={country} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {toggleAction.serviceState && (
        <div className="mt-5">
          <label
            htmlFor="serviceState"
            className="block text-sm font-bold text-gray-700"
          >
            {t("stepThree.serviceState")}{" "}
            <span className="text-orange-600">*</span>
          </label>
          <div className="mt-1">
            <select
              id="serviceState"
              name="serviceState"
              className="mt-1 p-2 border border-gray-300 rounded-md focus:outline-none w-full me-2"
              onChange={(e) => {
                const campaignParams: any = campaign.parameters;
                campaignParams["serviceState"] = e.target.value;
                setCampaign({
                  ...campaign,
                  parameters: campaignParams,
                });
              }}
            >
              <option value="">{t("stepThree.selectServiceState")}</option>
              <option value="serviceRequested">
                {t("stepThree.serviceRequested")}
              </option>
              <option value="inService">{t("stepThree.inService")}</option>
              <option value="contactPartner">
                {t("stepThree.contactPartner")}
              </option>
              <option value="serviceResponse">
                {t("stepThree.serviceResponse")}
              </option>
              <option value="serviceCancelled">
                {t("stepThree.serviceCancelled")}
              </option>
              <option value="serviceFinished">
                {t("stepThree.serviceFinished")}
              </option>
              <option value="serviceReady">
                {t("stepThree.serviceReady")}
              </option>
            </select>
          </div>
        </div>
      )}

      {Object.keys(toggleAction).every((key: string) => !toggleAction[key]) && (
        <div className="mt-5 text-red-500 font-normal border border-red-500 rounded-md p-3">
          {t("stepThree.selectAtLeastOneAction")}
        </div>
      )}

      <div className="mt-5 float-end">
        <button
          type="button"
          className="w-64 me-2 border-indigo-600 border-2 text-indigo-600 py-2 rounded-md focus:outline-none mb-2 mt-2"
          onClick={() => setStep(2)}
        >
          {t("common.back")}
        </button>
        <button
          disabled={
            addCampaignMutation.isLoading ||
            !Object.keys(campaign.parameters || {}).length
          }
          type="submit"
          className={`w-64 text-white py-2 rounded-md focus:outline-none ${
            addCampaignMutation.isLoading ||
            Object.keys(campaign.parameters || {}).length === 0
              ? "cursor-not-allowed bg-indigo-400"
              : "bg-indigo-600"
          } mb-2 mt-2`}
        >
          {t("common.save")}
        </button>
      </div>
    </div>
  );
};

export default StepThree;
